"use client"
import { useState } from "react"
import { toast } from "sonner"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useParams, useNavigate } from "react-router-dom"
import BackButton from "../../components/common/BackButton"

// Shadcn Components
import { Input } from "../../components/ui/input"
import { Textarea } from "../../components/ui/textarea"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../components/ui/form"
import { Card, CardContent, CardDescription, CardTitle } from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs"
import { Mail, MessageSquare } from "lucide-react"

// Icons
import { Send } from "lucide-react"

// Services
import { notifyAttendeesService } from "../../services/event/notifyAttendeesService"

// Hooks
import { useTenantNavigation } from "../../hooks/navigation/useTenantNavigation"

const formSchema = z.object({
    subject: z.string().min(1, "Subject is required"),
    content: z.string().min(1, "Content is required")
});

function EmailNotificationForm({ onSubmit, isSubmitting }: { onSubmit: (values: z.infer<typeof formSchema>) => Promise<void>, isSubmitting: boolean }) {
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            subject: "",
            content: ""
        }
    });

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col h-[400px]">
                <FormField
                    control={form.control}
                    name="subject"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-lg">Subject</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="Enter email subject"
                                    className="h-12 text-lg"
                                    style={{ fontSize: '1.125rem' }}
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <div className="flex-grow mt-6">
                    <FormField
                        control={form.control}
                        name="content"
                        render={({ field }) => (
                            <FormItem className="h-full flex flex-col">
                                <FormLabel className="text-lg">Content</FormLabel>
                                <FormControl className="flex-grow">
                                    <Textarea
                                        placeholder="Enter email body"
                                        className="resize-none h-full text-lg"
                                        style={{ fontSize: '1.125rem' }}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                <Button 
                    type="submit" 
                    className="w-full h-12 text-lg mt-6"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        "Sending..."
                    ) : (
                        <>
                            <Send className="mr-2 h-5 w-5" />
                            Send Email
                        </>
                    )}
                </Button>
            </form>
        </Form>
    );
}

function SMSNotificationForm({ onSubmit, isSubmitting }: { onSubmit: (values: { message: string }) => Promise<void>, isSubmitting: boolean }) {
    const form = useForm<{ message: string }>({
        resolver: zodResolver(z.object({
            message: z.string().min(1, "Message is required").max(160, "Message must be 160 characters or less")
        })),
        defaultValues: {
            message: ""
        }
    });

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col h-[400px]">
                <div className="space-y-6 flex-grow">
                    <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                            <FormItem className="flex-grow flex flex-col h-full">
                                <FormLabel className="text-lg">Message</FormLabel>
                                <FormControl className="flex-grow">
                                    <Textarea
                                        placeholder="Enter SMS message"
                                        className="resize-none h-full text-lg"
                                        style={{ fontSize: '1.125rem' }}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                <Button 
                    type="submit" 
                    className="w-full h-12 text-lg mt-6"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        "Sending..."
                    ) : (
                        <>
                            <Send className="mr-2 h-5 w-5" />
                            Send SMS
                        </>
                    )}
                </Button>
            </form>
        </Form>
    );
}

export default function NotifyAttendees() {
    const { uuid } = useParams<{ uuid: string }>();
    const navigate = useNavigate();
    const { getTenantPath } = useTenantNavigation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState<string>("email");

    async function handleEmailSubmit(values: z.infer<typeof formSchema>) {
        setIsSubmitting(true);
        try {
            const response = await notifyAttendeesService.notifyAttendees(uuid!, values);
            toast.success(response.message);
            setTimeout(() => {
                navigate(getTenantPath(`/event/${uuid}`));
            }, 1500);
        } catch (error) {
            toast.error("Failed to submit the form. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    }

    async function handleSMSSubmit(values: { message: string }) {
        setIsSubmitting(true);
        try {
            const response = await notifyAttendeesService.notifyAttendeesSMS(uuid!, values);
            toast.success(response.message);
            setTimeout(() => {
                navigate(getTenantPath(`/event/${uuid}`));
            }, 1500);
        } catch (error) {
            toast.error("Failed to send SMS. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <div className="pb-12 md:pb-6">
            <div className="mb-0 md:mb-6">
                <BackButton to={getTenantPath(`/event/${uuid}`)} text="Back to Event" />
            </div>

            <Card className="border-none bg-[#23d6e4] text-white">
                <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <CardTitle className="text-3xl font-bold mb-2 text-white">
                                Notify Attendees
                            </CardTitle>
                            <CardDescription className="text-white/80">
                                Send a notification to all attendees of this event
                            </CardDescription>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="mt-6">
                <CardContent className="p-6 pb-8">
                    <Tabs defaultValue="email" className="space-y-4" onValueChange={setActiveTab}>
                        <TabsList className="transition-none">
                            <TabsTrigger value="email" className="flex items-center gap-2 transition-none">
                                <Mail className="h-4 w-4" />
                                Email
                            </TabsTrigger>
                            <TabsTrigger value="sms" className="flex items-center gap-2 transition-none">
                                <MessageSquare className="h-4 w-4" />
                                SMS
                            </TabsTrigger>
                        </TabsList>

                        <div>
                            <TabsContent value="email" className="h-full">
                                <EmailNotificationForm onSubmit={handleEmailSubmit} isSubmitting={isSubmitting} />
                            </TabsContent>

                            <TabsContent value="sms" className="h-full">
                                <SMSNotificationForm onSubmit={handleSMSSubmit} isSubmitting={isSubmitting} />
                            </TabsContent>
                        </div>
                    </Tabs>
                </CardContent>
            </Card>
        </div>
    )
}
